import { computed, Ref, ref } from "@vue/composition-api";
import { InputContentSetUpdate } from "@scrile/api-provider/dist/api/ContentSetProvider";
import { Participant } from "@scrile/api-provider/dist/api/ParticipantProvider";
import providers from "@/lib/api";

const participantKey = (participant: Participant) =>
  participant.userId || participant.urlUuid || participant.email || participant.id;

/**
 * Owns participant state at the orchestrator level (like usePostFiles owns file state), so that
 * edits made before publishing survive the editor being minimized/unmounted while files upload.
 * The publish-time diff is applied via syncParticipants once the post's files are persisted.
 */
export default function usePostParticipants(postId: Ref<string>) {
  // Baseline as returned by the backend; the diff target for syncParticipants.
  const apiParticipants = ref<Participant[]>([]);
  // Working copy edited through the UI; not persisted until publish.
  const localParticipants = ref<Participant[]>([]);

  const participantsCount = computed(() => localParticipants.value.length);

  const getParticipants = async () => {
    const result = await providers.ParticipantProvider.search({ data: { postIds: [postId.value] } });
    apiParticipants.value = result;
    localParticipants.value = [...result];
  };

  const setParticipants = (list: Participant[]) => {
    localParticipants.value = list;
  };

  // A resent participant is already persisted, so update both the baseline and the working copy
  // to keep it out of the create/delete diff.
  const updateParticipant = (participant: Participant) => {
    const replace = (list: Participant[]) => list.map(p => (p.id === participant.id ? participant : p));
    apiParticipants.value = replace(apiParticipants.value);
    localParticipants.value = replace(localParticipants.value);
  };

  const syncParticipants = async (data: Omit<InputContentSetUpdate, "id">) => {
    if (!data.published) return;

    const apiIds = new Set(apiParticipants.value.map(participantKey));
    const localIds = new Set(localParticipants.value.map(participantKey));

    const toCreate = localParticipants.value.filter(p => !apiIds.has(participantKey(p)));
    const toDelete = apiParticipants.value.filter(p => !localIds.has(participantKey(p)));
    const [createdParticipants] = await Promise.all([
      Promise.all(
        toCreate.map(p =>
          providers.ParticipantProvider.create({
            data: {
              postId: postId.value,
              userId: p.userId || undefined,
              urlUuid: p.urlUuid || undefined,
              email: p.email || undefined,
            },
          })
        )
      ),
      Promise.all(toDelete.map(p => providers.ParticipantProvider.delete({ id: p.id }))),
    ]);

    const persistedByKey = new Map([
      ...apiParticipants.value.map(p => [participantKey(p), p] as const),
      ...createdParticipants.map(p => [participantKey(p), p] as const),
    ]);
    apiParticipants.value = localParticipants.value.map(p => persistedByKey.get(participantKey(p)) || p);
    localParticipants.value = [...apiParticipants.value];
  };

  const reset = () => {
    apiParticipants.value = [];
    localParticipants.value = [];
  };

  return {
    localParticipants,
    participantsCount,
    getParticipants,
    setParticipants,
    updateParticipant,
    syncParticipants,
    reset,
  };
}
